/**
 * Bitboard helpers: the board as 8 row masks (bit c of masks[r] = cell (r, c) is filled).
 * The solver, the deal fairness check and the bot all work on masks; the colored `board`
 * array in game.ts is only for rendering.
 */
import { SIDE, type PieceDef } from "./pieces";

export const FULL_ROW = (1 << SIDE) - 1;

export type Masks = number[];

export function boardToMasks(board: readonly number[]): Masks {
  const m: Masks = new Array<number>(SIDE).fill(0);
  for (let r = 0; r < SIDE; r++) for (let c = 0; c < SIDE; c++) if (board[r * SIDE + c]) m[r] = m[r]! | (1 << c);
  return m;
}

export function fits(m: Masks, p: PieceDef, r: number, c: number): boolean {
  if (r < 0 || c < 0 || r + p.h > SIDE || c + p.w > SIDE) return false;
  for (let i = 0; i < p.h; i++) if ((p.rows[i]! << c) & m[r + i]!) return false;
  return true;
}

export function anyPlacement(m: Masks, p: PieceDef): boolean {
  for (let r = 0; r + p.h <= SIDE; r++) for (let c = 0; c + p.w <= SIDE; c++) if (fits(m, p, r, c)) return true;
  return false;
}

export function place(m: Masks, p: PieceDef, r: number, c: number): void {
  for (let i = 0; i < p.h; i++) m[r + i] = m[r + i]! | (p.rows[i]! << c);
}

/** Full rows / columns of the current masks (does not modify them). */
export function fullLines(m: Masks): { rows: number[]; cols: number[] } {
  const rows: number[] = [];
  let colAnd = FULL_ROW;
  for (let r = 0; r < SIDE; r++) {
    if (m[r] === FULL_ROW) rows.push(r);
    colAnd &= m[r]!;
  }
  const cols: number[] = [];
  for (let c = 0; c < SIDE; c++) if (colAnd & (1 << c)) cols.push(c);
  return { rows, cols };
}

/** Clear all full rows and columns simultaneously; returns how many lines went. */
export function clearLines(m: Masks): number {
  const { rows, cols } = fullLines(m);
  if (!rows.length && !cols.length) return 0;
  let colMask = 0;
  for (const c of cols) colMask |= 1 << c;
  for (let r = 0; r < SIDE; r++) m[r] = rows.includes(r) ? 0 : m[r]! & ~colMask;
  return rows.length + cols.length;
}

export function popcount(n: number): number {
  let x = n;
  let k = 0;
  while (x) {
    x &= x - 1;
    k++;
  }
  return k;
}

export function filledCount(m: Masks): number {
  let n = 0;
  for (const r of m) n += popcount(r);
  return n;
}
